import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from '../utils/axios-config';
import {
  ALL_TAGS,
  TAGS_BY_TYPE,
  getTagById,
  getTagBySlug,
} from '@/data/forum-tags';

/**
 * useTags - Hook for managing forum tags
 *
 * Provides:
 * - Fetching tags from API (with fallback to static data)
 * - Creating/updating/deleting tags (admin)
 * - Tag search functionality
 * - Caching with React Query
 */

// Query keys
export const tagKeys = {
  all: ['tags'],
  list: () => [...tagKeys.all, 'list'],
  grouped: () => [...tagKeys.all, 'grouped'],
  popular: (limit) => [...tagKeys.all, 'popular', limit],
  search: (query) => [...tagKeys.all, 'search', query],
  detail: (tagId) => [...tagKeys.all, 'detail', tagId],
};

/**
 * Fetch all tags
 */
export function useTags(options = {}) {
  const { enabled = true, useStatic = false } = options;

  return useQuery({
    queryKey: tagKeys.list(),
    queryFn: async () => {
      if (useStatic) {
        return ALL_TAGS;
      }

      try {
        const response = await axios.get('/omniverse/tags');
        return response.data.tags || [];
      } catch (error) {
        // Fallback to static tags on error
        console.warn(
          'Failed to fetch tags from API, using static fallback:',
          error
        );
        return ALL_TAGS;
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 30 * 60 * 1000, // 30 minutes
    enabled,
    placeholderData: ALL_TAGS, // Show static tags while loading
  });
}

/**
 * Fetch tags grouped by type
 */
export function useTagsGrouped(options = {}) {
  const { enabled = true, useStatic = false } = options;

  return useQuery({
    queryKey: tagKeys.grouped(),
    queryFn: async () => {
      if (useStatic) {
        return TAGS_BY_TYPE;
      }

      try {
        const response = await axios.get('/omniverse/tags/grouped');
        return response.data.tagsByType || TAGS_BY_TYPE;
      } catch (error) {
        console.warn(
          'Failed to fetch grouped tags, using static fallback:',
          error
        );
        return TAGS_BY_TYPE;
      }
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
    enabled,
    placeholderData: TAGS_BY_TYPE,
  });
}

/**
 * Fetch popular tags
 */
export function usePopularTags(limit = 10, options = {}) {
  const { enabled = true } = options;

  return useQuery({
    queryKey: tagKeys.popular(limit),
    queryFn: async () => {
      try {
        const response = await axios.get(
          `/omniverse/tags/popular?limit=${limit}`
        );
        return response.data.tags || [];
      } catch (error) {
        console.warn('Failed to fetch popular tags:', error);
        // Return first N tags from static as fallback
        return ALL_TAGS.slice(0, limit);
      }
    },
    staleTime: 2 * 60 * 1000, // 2 minutes (more frequent updates)
    enabled,
  });
}

/**
 * Search tags
 */
export function useTagSearch(query, options = {}) {
  const { enabled = true, debounce = 300 } = options;

  return useQuery({
    queryKey: tagKeys.search(query),
    queryFn: async () => {
      if (!query || query.length < 2) {
        return [];
      }

      try {
        const response = await axios.get(
          `/omniverse/tags/search?q=${encodeURIComponent(query)}`
        );
        return response.data.tags || [];
      } catch (error) {
        console.warn('Failed to search tags:', error);
        // Fallback to static search
        const lowerQuery = query.toLowerCase();
        return ALL_TAGS.filter(
          (tag) =>
            tag.name.toLowerCase().includes(lowerQuery) ||
            tag.slug.toLowerCase().includes(lowerQuery)
        );
      }
    },
    enabled: enabled && query && query.length >= 2,
    staleTime: 30 * 1000, // 30 seconds for search results
  });
}

/**
 * Get single tag by ID
 */
export function useTag(tagId, options = {}) {
  const { enabled = true } = options;

  return useQuery({
    queryKey: tagKeys.detail(tagId),
    queryFn: async () => {
      try {
        const response = await axios.get(`/omniverse/tags/${tagId}`);
        return response.data.tag;
      } catch (error) {
        console.warn('Failed to fetch tag:', error);
        // Fallback to static
        return getTagById(tagId);
      }
    },
    enabled: enabled && !!tagId,
    placeholderData: () => getTagById(tagId),
  });
}

/**
 * Create a new tag (admin)
 */
export function useCreateTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (tagData) => {
      const response = await axios.post('/omniverse/tags', tagData);
      return response.data.tag;
    },
    onSuccess: () => {
      // Invalidate all tag queries
      queryClient.invalidateQueries({ queryKey: tagKeys.all });
    },
  });
}

/**
 * Update a tag (admin)
 */
export function useUpdateTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ tagId, data }) => {
      const response = await axios.put(`/omniverse/tags/${tagId}`, data);
      return response.data.tag;
    },
    onSuccess: (_, { tagId }) => {
      queryClient.invalidateQueries({ queryKey: tagKeys.all });
      queryClient.invalidateQueries({ queryKey: tagKeys.detail(tagId) });
    },
  });
}

/**
 * Delete a tag (admin)
 */
export function useDeleteTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (tagId) => {
      await axios.delete(`/omniverse/tags/${tagId}`);
      return tagId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tagKeys.all });
    },
  });
}

/**
 * Bulk create tags (admin)
 */
export function useBulkCreateTags() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (tags) => {
      const response = await axios.post('/omniverse/tags/bulk', { tags });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tagKeys.all });
    },
  });
}

// Re-export helper functions
export { getTagById, getTagBySlug };

export default useTags;
